import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { CustomerAddress, Order } from "../../../types";
import { orderService } from "../../../services/orderService";
import { customerService } from "../../../services/customerService";
import { joinShippingAddress, splitShippingAddress } from "../../../utils/shippingAddress";
import { Notify, useQuickEditSave } from "./useQuickEditSave";

interface ShippingAddressDialogProps {
    open: boolean;
    order: Order;
    onClose: () => void;
    onOrderChanged: (order: Order) => void;
    notify: Notify;
}

const addressLabel = (a: CustomerAddress) =>
    `${a.address_type} - ${a.street_address}, ${a.city}${a.is_default ? " (Default)" : ""}`;

/**
 * Change the order's shipping address the same two ways the order form
 * offers: pick one of the customer's saved addresses (which also makes it
 * their default, server-side) or type one in.
 */
export const ShippingAddressDialog: React.FC<ShippingAddressDialogProps> = ({
    open,
    order,
    onClose,
    onOrderChanged,
    notify,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [street, setStreet] = useState("");
    const [city, setCity] = useState("");
    /** Set only while the text came from a saved address (cleared on typing). */
    const [savedAddressId, setSavedAddressId] = useState("");
    const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const { saving, error, setError, save } = useQuickEditSave(order.order_id, onOrderChanged, notify);

    const current = splitShippingAddress(order.shipping_address);

    useEffect(() => {
        if (!open) return;
        setStreet(current.street);
        setCity(current.city);
        setSavedAddressId("");
        setError("");
        setAddresses([]);
        if (!order.customer_id) return;
        let cancelled = false;
        setLoadingAddresses(true);
        customerService
            .getCustomerAddresses(order.customer_id)
            .then((list) => !cancelled && setAddresses(list || []))
            .catch(() => !cancelled && setAddresses([]))
            .finally(() => !cancelled && setLoadingAddresses(false));
        return () => {
            cancelled = true;
        };
        // Reset only when the dialog opens, not on every refresh behind it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const pickSaved = (addressId: string) => {
        const address = addresses.find((a) => a.address_id === addressId);
        if (!address) return;
        setSavedAddressId(addressId);
        setStreet(address.street_address);
        setCity(address.city);
    };

    const typed = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setter(e.target.value);
        setSavedAddressId(""); // typed text is no longer that saved address
    };

    const filled = street.trim() !== "" && city.trim() !== "";
    const next = joinShippingAddress(street, city);
    const changed = next !== joinShippingAddress(current.street, current.city) || !!savedAddressId;

    const submit = async () => {
        if (!filled || !changed || saving) return;
        const saved = await save(
            () =>
                orderService.updateOrder(order.order_id, {
                    shipping_address: next,
                    ...(savedAddressId ? { customer_address_id: savedAddressId } : {}),
                }),
            "Shipping address updated",
        );
        if (saved) onClose();
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
            <DialogTitle>Shipping address</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    {order.consignment_id && (
                        <Alert severity="warning">
                            Consignment #{order.consignment_id} was booked with the old address — changing it here
                            doesn't update the courier.
                        </Alert>
                    )}

                    {loadingAddresses ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.secondary" }}>
                            <CircularProgress size={16} />
                            <span>Loading saved addresses…</span>
                        </Stack>
                    ) : (
                        addresses.length > 0 && (
                            <FormControl fullWidth>
                                <InputLabel id="order-saved-address-label">Select Saved Address</InputLabel>
                                <Select
                                    labelId="order-saved-address-label"
                                    label="Select Saved Address"
                                    value={savedAddressId}
                                    onChange={(e) => pickSaved(e.target.value)}
                                >
                                    {addresses.map((a) => (
                                        <MenuItem key={a.address_id} value={a.address_id}>
                                            {addressLabel(a)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    Choosing an address here also sets it as the customer's default once saved.
                                </FormHelperText>
                            </FormControl>
                        )
                    )}

                    <TextField
                        label="Street Address"
                        value={street}
                        onChange={typed(setStreet)}
                        required
                        multiline
                        minRows={2}
                        fullWidth
                        autoFocus={!order.customer_id}
                    />
                    <TextField label="City" value={city} onChange={typed(setCity)} required fullWidth />

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={() => void submit()} disabled={!filled || !changed || saving}>
                    {saving ? "Saving…" : "Save address"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
